import { getBrand } from '@/lib/host'
import type {
  AnalyticsSourceSystem,
  Screen,
  TrackOptions,
} from './types'

const ARIADNE_ADMIN_PATH_RE =
  /^\/(?:admin|chat|cockpit|eunomia|kratos|odysseus|peitho|pheme|thalia)(?:\/|$)/

interface ActionContext {
  screen: Screen
  metadata?: TrackOptions['metadata']
}

/**
 * Brand → `source_system`. The ONLY place a product name is assigned.
 *
 * The same Vite bundle is served from several CloudFront distributions,
 * and the echo relay's anti-spoofing gate is per-Origin (`allowed_origins`
 * per producer). The Origin is therefore the real trust boundary — but a
 * hardcoded `source_system` made the telemetry name ignore it, so rows
 * from any other product's Origin would land labelled `cuidari_app` and
 * enter the Cuidari lake by omission. Deriving the name from the resolved
 * brand puts the label back on the boundary that is actually enforced.
 *
 * `francisquini` is the operator console (Ariadne), not a product surface:
 * its rows are console telemetry, not a product's lake.
 *
 * A brand that is absent from this map is NOT defaulted — see
 * `analyticsSourceForPath`, which returns `null` (do not emit).
 */
export const BRAND_SOURCE_SYSTEMS: Record<string, AnalyticsSourceSystem> = {
  cuidari: 'cuidari_app',
  ibirami: 'ibirami_app',
  francisquini: 'ariadne',
}

/** Schema namespace prefix per source system (`<prefix>.<action>.v1`). */
const SOURCE_SCHEMA_PREFIX: Record<AnalyticsSourceSystem, string> = {
  cuidari_app: 'cuidari',
  ibirami_app: 'ibirami',
  ariadne: 'ariadne',
}

/**
 * Count of envelopes dropped because the brand could not be mapped to a
 * product. Exported through `readUnknownBrandDrops()` so the drop is
 * observable in tests and in the browser console instead of being a
 * silent `return`. Dependency-free on purpose: this runs on every emit.
 */
let unknownBrandDrops = 0

/** How many envelopes have been dropped for an unmappable brand. */
export function readUnknownBrandDrops(): number {
  return unknownBrandDrops
}

/** Test helper — resets the drop counter between cases. */
export function resetUnknownBrandDrops(): void {
  unknownBrandDrops = 0
}

/**
 * Record (and announce) one dropped envelope. Call sites must `return`
 * right after, WITHOUT enqueuing: a missing row is recoverable, a row
 * mislabelled with another product's name is not.
 */
export function noteUnknownBrandDrop(): void {
  unknownBrandDrops += 1
  if (typeof console !== 'undefined') {
    // Stable marker string — greppable in the browser console.
    console.warn('[analytics] unknown brand — envelope dropped')
  }
}

/**
 * Resolve the `source_system` for the current path.
 *
 * Admin PATHS stay `ariadne` regardless of brand: a cockpit route served
 * from a product host is console telemetry, not product telemetry.
 *
 * Everything else follows the resolved brand. `null` means the brand
 * could not be attributed to a product — DO NOT EMIT.
 */
export function analyticsSourceForPath(
  pathname: string,
): AnalyticsSourceSystem | null {
  if (ARIADNE_ADMIN_PATH_RE.test(pathname)) return 'ariadne'
  return BRAND_SOURCE_SYSTEMS[getBrand()] ?? null
}

export function schemaVersionForAction(
  action: string,
  sourceSystem: AnalyticsSourceSystem,
): string {
  return `${SOURCE_SCHEMA_PREFIX[sourceSystem]}.${action}.v1`
}

export function schemaVersionForSource(
  schemaVersion: string,
  sourceSystem: AnalyticsSourceSystem,
): string {
  // Legacy override rewrite: call sites that still pass an explicit
  // `ariadne.*` schema for a Cuidari surface get renamed. This is a
  // Cuidari-only migration — there is no legacy ibirami namespace.
  if (sourceSystem === 'cuidari_app' && schemaVersion.startsWith('ariadne.')) {
    return `cuidari.${schemaVersion.slice(8)}`
  }
  return schemaVersion
}

function openedActionForScreen(screen: Screen): string | null {
  if (screen === 'home') return 'home.opened'
  if (screen === 'bills') return 'accounts.opened'
  if (screen === 'historico') return 'history.opened'
  if (screen === 'profile') return 'profile.opened'
  if (screen === 'referrals') return 'referrals.opened'
  if (screen === 'onboarding') return 'onboarding.step.viewed'
  return null
}

export function canonicalActionForSource(
  action: string,
  sourceSystem: AnalyticsSourceSystem,
  context: ActionContext,
): string {
  // The renaming table below IS the Cuidari event registry. `ariadne`
  // (console) and `ibirami_app` pass through unchanged — inventing an
  // ibirami registry here would be guessing another product's taxonomy.
  if (sourceSystem !== 'cuidari_app') return action

  if (action === 'screen.viewed') {
    return openedActionForScreen(context.screen) ?? action
  }

  if (action === 'bill_form.submit_succeeded') {
    return context.metadata?.edit_mode === true ? 'bill.edited' : 'bill.created'
  }

  if (action === 'bill_detail.opened' || action === 'bill_row.tapped') {
    return 'bill_instance.viewed'
  }
  if (
    action.startsWith('bill_detail.instance_mark_paid') ||
    action === 'bill_card.mark_paid_tapped'
  ) {
    return 'payment.marked'
  }
  if (action === 'bill_detail.disable_tapped') return 'bill.disabled'
  if (action === 'profile.plan_card_tapped') return 'profile.plan.viewed'
  if (action === 'onboarding.started') return 'onboarding.step.viewed'
  if (
    action === 'onboarding.intro.continue' ||
    action === 'onboarding.tour.completed' ||
    action === 'onboarding.capture_bills.continue'
  ) {
    return 'onboarding.step.completed'
  }

  if (action.startsWith('onboarding.capture_bills.')) {
    const step = action.slice(25)
    if (step === 'viewed') return 'lab.opened'
    if (step === 'text_submitted') return 'lab.submit.started'
    if (step === 'saved') return 'lab.submit.completed'
    if (step === 'error' || step === 'low_confidence') return 'lab.submit.failed'
  }
  return action
}
